import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from '../i18n';
import { formatLocalTime } from '../utils/time';

interface RecentClaim {
  id:                 string;
  spot_id:            string;
  spot_number:        string;
  level_code:         string;
  level_label:        string;
  started_at:         string;
  ended_at:           string;
  duration_secs:      number;
  occupant_apartment: string;
}

function formatDuration(secs: number): string {
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

export default function RecentClaimsPage() {
  const navigate = useNavigate();
  const timezone = useAuthStore((s) => s.user?.building.timezone ?? 'UTC');
  const { t, language } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['spots', 'recent-claims'],
    queryFn: () =>
      apiClient
        .get<{ data: RecentClaim[] }>('/spots/mine/recent-claims?limit=10')
        .then((r) => r.data.data),
  });

  const claims = data ?? [];

  return (
    <div className="page" style={{ maxWidth: '430px', margin: '0 auto', fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>
      {/* ── Top bar ── */}
      <header className="topbar" style={{
        position:             'sticky',
        top:                  0,
        zIndex:               50,
        backgroundColor:      'rgba(248,249,250,0.85)',
        backdropFilter:       'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow:            '0 12px 32px -4px rgba(43,52,55,0.06)',
      }}>
        <button onClick={() => navigate(-1)} style={iconBtn} aria-label="Close">
          <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>close</span>
        </button>

        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 114 26"
            style={{ height: '26px', width: 'auto', display: 'block' }}
            aria-label="Lot'N'Go"
          >
            <text
              x="57" y="20"
              fontFamily="Manrope, system-ui, sans-serif"
              fontWeight="700" fontSize="20"
              fill="#005bc0" textAnchor="middle" letterSpacing="-0.4"
            >
              {"Lot'N'Go"}
            </text>
          </svg>
        </div>

        <div style={{ width: '40px', flexShrink: 0 }} />
      </header>

      {/* ── Body ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px 32px' }}>
        <div style={headerCard}>
          <span style={eyebrow}>{t('recent_claims_title')}</span>
          <h2 style={headerTitle}>{t('recent_claims_title')}</h2>
          <p style={subtitle}>{t('recent_claims_subtitle')}</p>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', color: '#586064', padding: '40px 0' }}>…</div>
        ) : claims.length === 0 ? (
          <div style={emptyCard}>
            <span
              className="material-symbols-outlined"
              style={{ color: '#586064', fontSize: '28px', marginBottom: '8px' }}
            >
              schedule
            </span>
            <p style={{ fontSize: '14px', color: '#586064', margin: 0 }}>
              {t('recent_claims_empty')}
            </p>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {claims.map((c) => (
              <li key={c.id} style={claimCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={spotLabelRow}>
                      <span
                        className="material-symbols-outlined"
                        style={{ color: '#005bc0', fontSize: '16px' }}
                        aria-hidden
                      >
                        local_parking
                      </span>
                      <span style={spotLabelText}>
                        {t(c.level_code) || c.level_label} · {c.spot_number}
                      </span>
                    </div>

                    <div style={apartmentLine}>
                      <span
                        className="material-symbols-outlined"
                        style={{ color: '#586064', fontSize: '16px' }}
                        aria-hidden
                      >
                        person
                      </span>
                      <span>{t('claim_apt_short')} {c.occupant_apartment}</span>
                    </div>

                    <div style={timeLine}>
                      {formatLocalTime(c.started_at, timezone, 'datetime-weekday', language)}
                      {' '}{t('claim_arrow')}{' '}
                      {formatLocalTime(c.ended_at,   timezone, 'datetime-weekday', language)}
                    </div>
                  </div>

                  <span style={durationPill}>{formatDuration(c.duration_secs)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── styles ──────────────────────────────────────────────────────────────────

const iconBtn: React.CSSProperties = {
  width:          '40px',
  height:         '40px',
  border:         'none',
  background:     'none',
  cursor:         'pointer',
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  borderRadius:   '9999px',
  color:          '#005bc0',
  flexShrink:     0,
};

const headerCard: React.CSSProperties = {
  background:   '#ffffff',
  borderRadius: '20px',
  padding:      '20px 22px',
  boxShadow:    '0 8px 24px -4px rgba(43,52,55,0.06)',
  marginBottom: '16px',
};

const eyebrow: React.CSSProperties = {
  fontSize:      '10px',
  fontWeight:    700,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color:         '#005bc0',
  display:       'block',
  marginBottom:  '6px',
};

const headerTitle: React.CSSProperties = {
  fontFamily:    "'Manrope', system-ui, sans-serif",
  fontSize:      '24px',
  fontWeight:    800,
  color:         '#2b3437',
  margin:        '0 0 10px',
  lineHeight:    1.2,
  letterSpacing: '-0.02em',
};

const subtitle: React.CSSProperties = {
  fontSize:   '13px',
  color:      '#586064',
  margin:     0,
  lineHeight: 1.5,
};

const emptyCard: React.CSSProperties = {
  background:    '#ffffff',
  borderRadius:  '20px',
  padding:       '32px 22px',
  textAlign:     'center',
  boxShadow:     '0 8px 24px -4px rgba(43,52,55,0.06)',
  display:       'flex',
  flexDirection: 'column',
  alignItems:    'center',
};

const claimCard: React.CSSProperties = {
  background:   '#ffffff',
  borderRadius: '16px',
  padding:      '14px 16px',
  boxShadow:    '0 4px 16px -4px rgba(43,52,55,0.05)',
  marginBottom: '10px',
};

const spotLabelRow: React.CSSProperties = {
  display:      'flex',
  alignItems:   'center',
  gap:          '6px',
  marginBottom: '4px',
};

const spotLabelText: React.CSSProperties = {
  fontFamily:    "'Manrope', system-ui, sans-serif",
  fontSize:      '15px',
  fontWeight:    700,
  color:         '#2b3437',
  letterSpacing: '-0.01em',
};

const apartmentLine: React.CSSProperties = {
  display:      'flex',
  alignItems:   'center',
  gap:          '6px',
  fontSize:     '13px',
  fontWeight:   600,
  color:        '#2b3437',
  marginBottom: '3px',
};

const timeLine: React.CSSProperties = {
  fontSize:   '12px',
  color:      '#586064',
  lineHeight: 1.4,
};

const durationPill: React.CSSProperties = {
  display:       'inline-flex',
  alignItems:    'center',
  background:    '#dde8ff',
  color:         '#005bc0',
  fontSize:      '12px',
  fontWeight:    700,
  fontFamily:    "'Manrope', system-ui, sans-serif",
  padding:       '4px 10px',
  borderRadius:  '9999px',
  whiteSpace:    'nowrap',
  flexShrink:    0,
};
